import { useEffect, useRef, useState, useCallback } from 'react';
import { FamlaServices } from '../../services';
import { config } from '../../services/config';

// ─── Couleurs d’avatar déterministes (nom / seed) ─────────────────────────────

function hashString(seed: string): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** HSL → hex `#rrggbb` (compatible avec `toRgba` de la sidebar). */
function hslToHex(h: number, s: number, l: number): string {
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hue < 60) {
    r = c; g = x;
  } else if (hue < 120) {
    r = x; g = c;
  } else if (hue < 180) {
    g = c; b = x;
  } else if (hue < 240) {
    g = x; b = c;
  } else if (hue < 300) {
    r = x; b = c;
  } else {
    r = c; b = x;
  }
  const toByte = (v: number) => Math.round((v + m) * 255);
  return `#${[toByte(r), toByte(g), toByte(b)]
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('')}`;
}

function colorFromHue(hue: number, hash: number): string {
  const sat = 55 + (hash % 20); // 55–74 %
  const light = 48 + ((hash >> 8) % 14); // 48–61 %
  return hslToHex(hue % 360, sat, light);
}

/**
 * Couleur d’avatar stable à partir d’un seed (idéalement le nom complet).
 * Hex sur 360° de teinte — distinct, et compatible avec les convertisseurs rgba existants.
 */
export function getUserColor(seed: string): string {
  const key = (seed || '').trim().toLowerCase();
  if (!key) return '#E57373';
  const hash = hashString(key);
  return colorFromHue(hash % 360, hash);
}

/**
 * Attribue une teinte unique par nom distinct dans une liste (sidebar collaborateurs).
 * Préférence hash du nom, puis décalage si collision de teinte dans le lot.
 */
export function assignUniqueAvatarColors(names: ReadonlyArray<string>): Map<string, string> {
  const uniqueKeys = [
    ...new Set(
      names
        .map((n) => n.trim().toLowerCase())
        .filter(Boolean),
    ),
  ].sort();

  const usedHues = new Set<number>();
  const map = new Map<string, string>();

  for (const key of uniqueKeys) {
    const hash = hashString(key);
    let hue = hash % 360;
    let attempts = 0;
    while (usedHues.has(hue) && attempts < 360) {
      hue = (hue + 37) % 360;
      attempts += 1;
    }
    usedHues.add(hue);
    map.set(key, colorFromHue(hue, hash));
  }

  return map;
}

export function getUserInitials(fullname: string): string {
  if (!fullname) return '?';
  const parts = fullname.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}



// ─── Types ────────────────────────────────────────────────────────────────────
export interface ActiveUser {
  user_id: string;
  fullname: string;
  email?: string;
  avatar?: string;
  role?: string;
  color: string;
  initials: string;
  connected_at?: string;
  cursor_position?: { x: number; y: number } | null;
}

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

interface UseCollaborationOptions {
  mapId: string;
  userId: string;
  fullname: string;
  email?: string;
  role?: string;
  token: string;
  processId: string;
  onMapUpdate?: (editData: any) => void;
  onUserJoined?: (user: ActiveUser) => void;
  onUserLeft?: (user: ActiveUser) => void;
  onPermissionGranted?: () => void;
}

interface UseCollaborationReturn {
  activeUsers: ActiveUser[];
  saveStatus: SaveStatus;
  isConnected: boolean;
  triggerAutoSave: (data: any) => void;
  broadcastLocalEdit: (action: string, modifications: any) => void;
  sendCursorMove: (x: number, y: number) => void;
}


// ─── Hook principal ──────────────────────────────────────────────────────────
export function useCollaboration({
  mapId,
  userId,
  fullname,
  email,
  role,
  token,
  processId,
  onMapUpdate,
  onUserJoined,
  onUserLeft,
  onPermissionGranted,
}: UseCollaborationOptions): UseCollaborationReturn {
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingDataRef = useRef<any>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;

  // Ref stable pour le token uniquement (peut être rafraîchi sans changer mapId/userId).
  // Ne PAS faire ça pour mapId : voir commentaire dans le cleanup ci-dessous.
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // ── Clé de session unique par montage effectif ─────────────────────────────
  // Chaque fois que le useEffect se lance vraiment (après le délai de stabilisation),
  // on incrémente cette clé. Les handlers d'un WS périmé comparent leur clé à
  // la clé courante et s'ignorent s'ils sont obsolètes.
  const sessionKeyRef = useRef(0);

  // Callbacks stables en ref pour éviter les re-render cycles
  const onMapUpdateRef = useRef(onMapUpdate);
  const onUserJoinedRef = useRef(onUserJoined);
  const onUserLeftRef = useRef(onUserLeft);
  const onPermissionGrantedRef = useRef(onPermissionGranted);
  useEffect(() => { onMapUpdateRef.current = onMapUpdate; }, [onMapUpdate]);
  useEffect(() => { onUserJoinedRef.current = onUserJoined; }, [onUserJoined]);
  useEffect(() => { onUserLeftRef.current = onUserLeft; }, [onUserLeft]);
  useEffect(() => { onPermissionGrantedRef.current = onPermissionGranted; }, [onPermissionGranted]);

  // Enrichit un utilisateur brut avec couleur et initiales
  const enrichUser = useCallback((raw: any): ActiveUser => ({
    user_id: raw.user_id,
    fullname: raw.fullname || 'Utilisateur',
    email: raw.email,
    avatar: raw.avatar,
    role: raw.role,
    color: getUserColor(raw.fullname || raw.email || raw.user_id || ''),
    initials: getUserInitials(raw.fullname || ''),
    connected_at: raw.connected_at,
    cursor_position: raw.cursor_position || null,
  }), []);

  // ── Connexion WebSocket (une seule connexion stable) ──────────────────────
  const connectWs = useCallback((sessionKey: number) => {
    const baseUrl = config.api.notificationWsUrl;
    const roomId = `map:${mapId}`;
    const params = new URLSearchParams({
      user_id: userId,
      fullname,
      ...(email ? { email } : {}),
      ...(role ? { role } : {}),
    });
    const url = `${baseUrl}/ws/${roomId}?${params.toString()}`;

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      // Session expirée → ferme sans bruit
      if (sessionKeyRef.current !== sessionKey) { ws.close(); return; }
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;

      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
        } else {
          clearInterval(pingInterval);
        }
      }, 30000);
      (ws as any)._pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      if (sessionKeyRef.current !== sessionKey) return;
      try {
        const message = JSON.parse(event.data);
        handleMessage(message, sessionKey);
      } catch {
        // message non-JSON ignoré
      }
    };

    ws.onclose = () => {
      if ((ws as any)._pingInterval) clearInterval((ws as any)._pingInterval);
      // Session expirée → on ne fait rien (le cleanup s'en est chargé)
      if (sessionKeyRef.current !== sessionKey) return;

      setIsConnected(false);

      // Reconnexion avec backoff exponentiel
      if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          if (sessionKeyRef.current === sessionKey) {
            connectWs(sessionKey);
          }
        }, delay);
      }
    };

    ws.onerror = () => {
      if (sessionKeyRef.current !== sessionKey) return;
      setIsConnected(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, userId, fullname, email, role]);

  // ── Traitement des messages reçus ─────────────────────────────────────────
  const handleMessage = useCallback((message: any, sessionKey: number) => {
    if (sessionKeyRef.current !== sessionKey) return;

    switch (message.type) {
      case 'room_state': {
        setActiveUsers((message.active_users || []).map(enrichUser));
        break;
      }

      case 'user_joined': {
        const newUser = enrichUser(message.user);
        if (message.active_users) {
          setActiveUsers((message.active_users || []).map(enrichUser));
        } else {
          setActiveUsers(prev => {
            if (prev.find(u => u.user_id === newUser.user_id)) return prev;
            return [...prev, newUser];
          });
        }
        if (newUser.user_id !== userId) {
          onUserJoinedRef.current?.(newUser);
        }
        break;
      }

      case 'user_left': {
        const leftUser = enrichUser(message.user);
        if (message.active_users) {
          setActiveUsers((message.active_users || []).map(enrichUser));
        } else {
          setActiveUsers(prev => prev.filter(u => u.user_id !== leftUser.user_id));
        }
        if (leftUser.user_id !== userId) {
          onUserLeftRef.current?.(leftUser);
        }
        break;
      }

      case 'map_edit': {
        if (onMapUpdateRef.current && message.edit_data) {
          // Le service renvoie edit_data avec user_id dedans
          onMapUpdateRef.current(message.edit_data);
        }
        break;
      }

      case 'cursor_move': {
        if (message.user_id !== userId) {
          setActiveUsers(prev =>
            prev.map(u =>
              u.user_id === message.user_id
                ? { ...u, cursor_position: message.cursor_position }
                : u
            )
          );
        }
        break;
      }

      case 'permission_update': {
        // Déclenché par le backend quand un droit d'édition est accordé à un utilisateur
        if (message.user_id === userId) {
          onPermissionGrantedRef.current?.();
        }
        break;
      }

      default:
        break;
    }
  }, [enrichUser, userId]);

  // ── Transforme le payload frontend en format backend ─────────────────────
  // Frontend: { swimlaneData (obj), blocks (array), groups (array) }
  // Backend:  { line (JSON string), swimlane (JSON string), second (JSON string) }
  const buildAutoSavePayload = (data: any) => ({
    line: JSON.stringify(data.swimlaneData),
    swimlane: JSON.stringify(data.blocks),
    second: JSON.stringify(data.groups),
  });

  // ── Auto-save (style diagram.net : debounce 2s) ───────────────────────────
  const triggerAutoSave = useCallback((data: any) => {
    pendingDataRef.current = data;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setSaveStatus('pending');

    autoSaveTimerRef.current = setTimeout(async () => {
      if (!pendingDataRef.current || !mapId || !token) return;
      setSaveStatus('saving');

      try {
        const result = await FamlaServices(
          'api_host',
          `/mapping/autosave/${mapId}`,
          'PUT',
          buildAutoSavePayload(pendingDataRef.current),
          token
        );

        if (result.status === 200 || result.status === 201) {
          setSaveStatus('saved');
          pendingDataRef.current = null;
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 5000);
        }
      } catch {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus('idle'), 5000);
      }
    }, 2000);
  }, [mapId, token]);

  // ── Broadcast d'une édition locale ───────────────────────────────────────
  const broadcastLocalEdit = useCallback((action: string, modifications: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'map_edit_local',
        data: {
          action,
          modifications,
          // user_id au niveau racine pour filtrage côté récepteur
          user_id: modifications?.user_id || userId,
        },
      }));
    }
  }, [userId]);

  // ── Envoi de la position curseur ──────────────────────────────────────────
  const sendCursorMove = useCallback((x: number, y: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor_move',
        data: { x, y },
      }));
    }
  }, []);

  // ── Lifecycle : délai de stabilisation anti-StrictMode ────────────────────
  // En dev (StrictMode), React fait : mount → cleanup → mount.
  // On attend 80ms avant de créer le WS. Si le cleanup arrive dans ce délai
  // (ce qui est le cas du 1er mount fictif du StrictMode), on annule.
  // Un vrai démontage permanent déclenche un cleanup au-delà de ce délai.
  useEffect(() => {
    if (!mapId || !userId) return;

    // Invalide l'éventuelle session précédente
    sessionKeyRef.current += 1;
    const mySessionKey = sessionKeyRef.current;

    // Annule les timers orphelins
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Délai de stabilisation : si le composant est démonté avant 80ms
    // (cas StrictMode), le WebSocket n'est jamais créé.
    const stabilizeTimer = setTimeout(() => {
      // Vérifie que cette session est toujours la courante
      if (sessionKeyRef.current !== mySessionKey) return;
      connectWs(mySessionKey);
    }, 80);

    return () => {
      // Cleanup : invalide immédiatement cette session
      sessionKeyRef.current += 1;
      clearTimeout(stabilizeTimer);

      // ── FLUSH auto-save en attente ─────────────────────────────────────────
      // Si une modification est en attente (debounce non encore écoulé) au moment
      // du démontage (ex: changement de map), on l'envoie immédiatement en
      // fire-and-forget pour ne pas perdre les données.
      //
      // IMPORTANT : on utilise `mapId` capturé dans la closure de CET effet
      // (donc l'ANCIENNE carte qu'on quitte), et surtout PAS une ref partagée
      // mise à jour par un autre useEffect([mapId]) — sinon cette ref est déjà
      // réécrite avec le NOUVEL id au moment où ce cleanup s'exécute (React
      // exécute le setup des effets déclarés avant, puis le cleanup des effets
      // suivants), ce qui envoyait les données de la carte quittée vers le
      // document Mongo de la carte cible et corrompait les deux cartes.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      if (pendingDataRef.current && mapId && tokenRef.current) {
        const flushPayload = buildAutoSavePayload(pendingDataRef.current);
        const flushMapId = mapId;
        const flushToken = tokenRef.current;
        pendingDataRef.current = null;

        // fire-and-forget : le browser continue la requête même après unmount
        FamlaServices('api_host', `/mapping/autosave/${flushMapId}`, 'PUT', flushPayload, flushToken)
          .catch(() => { /* flush silencieux */ });
      }

      // Annule le timer de reconnexion WebSocket
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      // Ferme proprement le WebSocket courant
      if (wsRef.current) {
        if ((wsRef.current as any)._pingInterval) {
          clearInterval((wsRef.current as any)._pingInterval);
        }
        wsRef.current.close();
        wsRef.current = null;
      }

      setIsConnected(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, userId]);

  return {
    activeUsers,
    saveStatus,
    isConnected,
    triggerAutoSave,
    broadcastLocalEdit,
    sendCursorMove,
  };
}
